import React, { useEffect, useState } from "react";
import { BellOff, Bell, ShieldAlert, Radio, CheckCircle2, AlertCircle, Loader2, Lock, ChevronDown } from "lucide-react";
import useApi from "../../../CustomHooks/useApi";

const MONO = "font-['IBM_Plex_Mono']";
const SANS = "font-['IBM_Plex_Sans']";

const MODES = [
  { value: "NONE", label: "NONE", desc: "No prompt shown.", icon: BellOff, color: "#64748B" },
  { value: "MODERATE", label: "MODERATE", desc: "Dismissible prompt below Minimum Version.", icon: Bell, color: "#D97706" },
  { value: "FORCE", label: "FORCE", desc: "Blocks the app below Minimum Version.", icon: ShieldAlert, color: "#DC2626" },
];

const MESSAGE_OPTIONS = [
  "A new version of BingoPay is available. Update now to enjoy the latest features and improvements.",
  "This update includes important security enhancements. Please update BingoPay to keep your account safe.",
  "A mandatory update is required to continue using BingoPay. Please update now.",
  "We've resolved key issues in this release. Update BingoPay now for a smoother experience.",
  "Your BingoPay app version is outdated and no longer supported. Please update to continue.",
];

const inputClass =
  "w-full bg-gray-50 border border-gray-200 rounded-lg px-3.5 py-2.5 text-base text-gray-900 " +
  "placeholder:text-gray-400 outline-none focus:border-[#0A2A4F] focus:ring-2 focus:ring-[#0A2A4F]/20 focus:bg-white transition";

const FieldLabel = ({ children }) => (
  <label className={`${MONO} block mb-1.5 text-xs font-semibold tracking-[0.12em] text-gray-400`}>
    {children}
  </label>
);

const PhonePreview = ({ mode, message }) => {
  const idle = mode === "NONE";
  return (
    <div className="relative w-[220px] h-[440px] rounded-[34px] border-[8px] border-gray-800 bg-black overflow-hidden shrink-0">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-b-xl z-20" />
      <div className="absolute inset-0 bg-[#F2F4F8] flex flex-col">
        <div className="h-9 bg-[#0A2A4F] flex items-center px-4 shrink-0">
          <div className="w-16 h-2 rounded bg-white/25" />
        </div>
        <div className={`flex-1 p-3.5 space-y-2.5 transition-opacity ${idle ? "opacity-100" : "opacity-25"}`}>
          <div className="h-11 rounded-lg bg-white" />
          <div className="grid grid-cols-3 gap-2">
            <div className="h-9 rounded bg-white" />
            <div className="h-9 rounded bg-white" />
            <div className="h-9 rounded bg-white" />
          </div>
          <div className="h-16 rounded-lg bg-white" />
          <div className="h-16 rounded-lg bg-white" />
        </div>

        {mode === "MODERATE" && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/45 p-4">
            <div className="w-full rounded-xl bg-white p-4 text-center shadow-xl">
              <div className="mx-auto mb-2 flex h-8 w-8 items-center justify-center rounded-full bg-amber-100">
                <Bell size={15} className="text-amber-600" />
              </div>
              <p className={`${SANS} text-xs font-bold text-gray-900`}>Update Available</p>
              <p className={`${SANS} mt-1.5 text-[10px] leading-snug text-gray-500 line-clamp-3`}>
                {message || "A new version of the app is available."}
              </p>
              <div className={`${SANS} mt-3 rounded bg-[#0A2A4F] py-2 text-[10px] font-bold text-white`}>
                Update Now
              </div>
              <p className={`${SANS} mt-1.5 text-[10px] text-gray-400`}>Later</p>
            </div>
          </div>
        )}

        {mode === "FORCE" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white p-5 text-center">
            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-red-100">
              <ShieldAlert size={20} className="text-red-600" />
            </div>
            <p className={`${SANS} text-xs font-bold text-gray-900`}>Update Required</p>
            <p className={`${SANS} mt-1.5 text-[10px] leading-snug text-gray-500 line-clamp-4`}>
              {message || "Please update to continue using the app."}
            </p>
            <div className={`${SANS} mt-4 w-full rounded bg-[#0A2A4F] py-2.5 text-[10px] font-bold text-white`}>
              Update Now
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const DEFAULT_STORE_URL = "https://play.google.com/store/apps/details?id=com.bingopays&hl=en_IN";

const PlatformVersionCard = ({ platform }) => {
  const [formData, setFormData] = useState({
    latestVersion: "",
    minVersion: "",
    updateMode: "NONE",
    storeUrl: DEFAULT_STORE_URL,
    message: MESSAGE_OPTIONS[0],
  });
  const [status, setStatus] = useState({ type: "", text: "" });

  const { callApi: fetchConfig, loading: fetching } = useApi("/app/version-check", "GET");
  const { callApi: saveConfig, loading: saving } = useApi("/admin/app-version", "PUT");

  useEffect(() => {
    fetchConfig(null, { platform })
      .then((res) => {
        setFormData({
          latestVersion: res.latestVersion || "",
          minVersion: res.minVersion || "",
          updateMode: res.updateMode || "NONE",
          storeUrl: DEFAULT_STORE_URL,
          message: MESSAGE_OPTIONS.includes(res.message) ? res.message : MESSAGE_OPTIONS[0],
        });
      })
      .catch(() => {
        // No row for this platform yet — leave the form blank, first save creates it.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const setMode = (value) => setFormData((p) => ({ ...p, updateMode: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", text: "" });
    try {
      await saveConfig({ platform, ...formData });
      setStatus({ type: "success", text: `Broadcasting to the ${platform} fleet — live on next app open.` });
    } catch (err) {
      setStatus({ type: "error", text: `Broadcast failed: ${err?.message || "please try again."}` });
    }
  };

  const activeMode = MODES.find((m) => m.value === formData.updateMode) || MODES[0];
  const activeIndex = MODES.findIndex((m) => m.value === formData.updateMode);
  const platformLabel = platform.charAt(0).toUpperCase() + platform.slice(1);

  return (
    <div className={`${SANS} overflow-hidden rounded-3xl border border-gray-100 bg-white text-gray-900 shadow-sm`}>
      {/* Eyebrow */}
      <div className="flex items-center justify-between px-8 pt-8">
        <div className={`${MONO} flex items-center gap-2 text-sm tracking-[0.2em] text-gray-400`}>
          <Radio size={16} />
          {platform.toUpperCase()} · FLEET CONTROL
        </div>
        <div
          className={`${MONO} flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold tracking-wide`}
          style={{ borderColor: `${activeMode.color}40`, color: activeMode.color, background: `${activeMode.color}0D` }}
        >
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: activeMode.color }} />
          {activeMode.label}
        </div>
      </div>

      <div className="px-8 pb-6 pt-3">
        <h1 className="text-3xl font-semibold text-gray-900">Update Broadcast</h1>
        <p className="mt-2 max-w-md text-base text-gray-500">
          Set what every {platformLabel} user sees next time they open the app — no store release needed.
        </p>
      </div>

      <div className="grid grid-cols-1 border-t border-gray-100 lg:grid-cols-[1fr_320px]">
        {/* Controls */}
        <form onSubmit={handleSubmit} className="space-y-6 border-b border-gray-100 p-8 lg:border-b-0 lg:border-r">
          {fetching && (
            <p className={`${MONO} flex items-center gap-2 text-sm text-gray-400`}>
              <Loader2 size={14} className="animate-spin" /> READING CURRENT BROADCAST…
            </p>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel>Latest Version</FieldLabel>
              <input
                type="text"
                name="latestVersion"
                value={formData.latestVersion}
                onChange={handleChange}
                required
                placeholder="2.2.0"
                className={`${inputClass} ${MONO} text-base py-3`}
              />
            </div>
            <div>
              <FieldLabel>Minimum Version</FieldLabel>
              <input
                type="text"
                name="minVersion"
                value={formData.minVersion}
                onChange={handleChange}
                required
                placeholder="2.2.0"
                className={`${inputClass} ${MONO} text-base py-3`}
              />
            </div>
          </div>

          <div>
            <FieldLabel>Update Mode</FieldLabel>
            <div className="flex items-start pt-1">
              {MODES.map((m, i) => {
                const Icon = m.icon;
                const active = formData.updateMode === m.value;
                return (
                  <React.Fragment key={m.value}>
                    {i > 0 && (
                      <div
                        className="mt-[22px] h-0.5 flex-1 transition-colors duration-300"
                        style={{ background: activeIndex >= i ? m.color : "#E5E7EB" }}
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => setMode(m.value)}
                      className="flex shrink-0 flex-col items-center gap-2"
                    >
                      <span
                        className="flex h-11 w-11 items-center justify-center rounded-full border-2 transition-colors"
                        style={{
                          borderColor: active ? m.color : "#E5E7EB",
                          background: active ? `${m.color}15` : "#F9FAFB",
                          color: active ? m.color : "#9CA3AF",
                        }}
                      >
                        <Icon size={20} />
                      </span>
                      <span
                        className={`${MONO} text-sm font-semibold tracking-wide`}
                        style={{ color: active ? m.color : "#9CA3AF" }}
                      >
                        {m.label}
                      </span>
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
            <p className="mt-2 text-base text-gray-500">{activeMode.desc}</p>
          </div>

          <div>
            <FieldLabel>Store URL</FieldLabel>
            <div className="relative">
              <input
                type="text"
                value={formData.storeUrl}
                readOnly
                className={`${inputClass} cursor-not-allowed bg-gray-100 pr-10 text-gray-500`}
              />
              <Lock size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
          </div>

          <div>
            <FieldLabel>Message</FieldLabel>
            <div className="relative">
              <select
                name="message"
                value={formData.message}
                onChange={handleChange}
                className={`${inputClass} appearance-none pr-10`}
              >
                {MESSAGE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              <ChevronDown size={18} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
          </div>

          {status.text && (
            <div
              className={`flex items-start gap-2 rounded-lg border px-3.5 py-2.5 text-sm font-medium ${
                status.type === "success"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-red-200 bg-red-50 text-red-600"
              }`}
            >
              {status.type === "success" ? (
                <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
              ) : (
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
              )}
              {status.text}
            </div>
          )}

          <button
            type="submit"
            disabled={saving}
            className={`w-full rounded-lg py-3.5 text-base font-bold text-white transition ${
              saving ? "cursor-not-allowed bg-gray-300" : "bg-[#0A2A4F] hover:bg-[#0d3562] shadow-sm"
            }`}
          >
            {saving ? "Broadcasting…" : `Broadcast to ${platformLabel}`}
          </button>
        </form>

        {/* Live preview */}
        <div className="flex flex-col items-center justify-center gap-4 bg-gray-50 p-8">
          <PhonePreview mode={formData.updateMode} message={formData.message} />
          <p className={`${MONO} text-center text-sm tracking-[0.15em] text-gray-400`}>LIVE PREVIEW</p>
        </div>
      </div>
    </div>
  );
};

export default PlatformVersionCard;
